/**
 * PremiumStatCards - Componente genérico de cards de estatísticas clicáveis
 * Reutilizável em todas as telas CRUD
 *
 * Props:
 * - filters: Array de objetos com { value, label, count, color, Icon }
 * - activeFilter: string (valor do filtro ativo)
 * - onFilterChange: function (callback quando um filtro é clicado)
 */

import React from 'react';
import {
 Box, Paper, Stack, Typography, useMediaQuery, useTheme 
} from '@mui/material';
import { usePremiumTokens } from '../../theme/usePremiumTokens';

const PremiumStatCards = ({
  filters,
  activeFilter = '',
  onFilterChange,
}) => {
  const theme = useTheme();
  const tokens = usePremiumTokens();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!filters || filters.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: { xs: 1, sm: 1.5 },
        flexWrap: 'wrap',
        flexShrink: 0,
      }}
    >
      {filters.map((f) => {
        const IconComponent = f.Icon;
        const isActive = activeFilter === f.value;

        return (
          <Paper
            key={f.value}
            onClick={() => onFilterChange?.(f.value)}
            sx={{
              flex: isMobile ? '1 1 calc(50% - 4px)' : '1 1 120px',
              maxWidth: isMobile ? 'calc(50% - 4px)' : 200,
              p: { xs: 1, sm: 1.5 },
              cursor: 'pointer',
              backgroundColor: tokens?.surface?.glass,
              backdropFilter: 'blur(12px)',
              borderRadius: tokens?.radius?.lg,
              border: isActive
                ? `2px solid ${f.color}`
                : `1px solid ${tokens?.border?.subtle}`,
              bgcolor: isActive
                ? `${f.color}18`
                : tokens?.surface?.glass,
              transition: tokens?.transitions?.all,
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: tokens?.shadows?.md,
                borderColor: f.color,
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Box
                sx={{
                  p: { xs: 0.5, sm: 0.75 },
                  borderRadius: 1.5,
                  bgcolor: `${f.color}20`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {IconComponent && (
                  <IconComponent sx={{ color: f.color, fontSize: { xs: 18, sm: 22 } }} />
                )}
              </Box>
              <Box>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  fontWeight="bold"
                  lineHeight={1.2}
                  sx={{ color: tokens?.text?.primary }}
                >
                  {f.count}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: tokens?.text?.secondary,
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }}
                >
                  {f.label}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        );
      })}
    </Box>
  );
};

export default React.memo(PremiumStatCards);
